/**
 * Pure helpers over the `my-permissions` payload (Task 5, ERP Users & Access
 * Rights epic). No network/React here — see useMyPermissions.ts for the hook.
 *
 * `undefined` means "still loading" for the per-object checks: the server
 * enforces regardless, so failing open here only affects what the UI shows
 * before the first response, never actual access. `isUserAdmin`/
 * `hasDesignerAccess` fail CLOSED on `undefined` — those gate admin-only UI
 * chrome that should stay hidden until we know for sure.
 */
export interface ConfiguredLanguage {
  code: string
  name: string
  isDefault: boolean
}

export interface MyPermissions {
  is_superuser: boolean
  is_owner: boolean
  is_member: boolean
  full_access: boolean
  read_all: boolean
  designer_access: boolean
  user_admin: boolean
  objects: Record<string, string[]>
  languages?: ConfiguredLanguage[]
  locale?: string | null
}

/** Users/Roles are implicitly readable for any member (mirrors
 * core/rbac/service.py::describe_permissions), even if a caller builds a
 * partial MyPermissions with an empty `objects` map. */
const IMPLICIT_MEMBER_READABLE = new Set(['Users', 'Roles'])

function opsFor(p: MyPermissions, objectName: string): string[] {
  return p.objects?.[objectName] ?? []
}

export function canRead(p: MyPermissions | undefined, objectName: string): boolean {
  if (!p) return true
  if (p.full_access || p.read_all) return true
  if (p.is_member && IMPLICIT_MEMBER_READABLE.has(objectName)) return true
  return opsFor(p, objectName).includes('read')
}

export function canCreate(p: MyPermissions | undefined, objectName: string): boolean {
  if (!p) return true
  if (p.full_access) return true
  return opsFor(p, objectName).includes('create')
}

export function canUpdate(p: MyPermissions | undefined, objectName: string): boolean {
  if (!p) return true
  if (p.full_access) return true
  return opsFor(p, objectName).includes('update')
}

export function canDelete(p: MyPermissions | undefined, objectName: string): boolean {
  if (!p) return true
  if (p.full_access) return true
  return opsFor(p, objectName).includes('delete')
}

export function isUserAdmin(p: MyPermissions | undefined): boolean {
  return !!p?.user_admin
}

/**
 * Object-aware "can I write this record" gate. `Users` always resolves through
 * `isUserAdmin` — `describe_permissions` pins `objects['Users'] = ['read']`
 * even for a user admin, so a literal create/update/delete check would hide
 * every write affordance from the people meant to manage accounts. `Roles` is
 * always false — the runtime router 403s every Roles write, including for the
 * owner and a superuser. Every other object uses the ordinary per-op RBAC
 * check. Single source of truth for RuntimeFormRenderer's Save/Delete/
 * Reset-password gating — do not re-special-case these names inline elsewhere.
 */
export function canWriteRecord(
  p: MyPermissions | undefined,
  objectName: string,
  op: 'create' | 'update' | 'delete',
): boolean {
  // Roles are a read-only projection of Designer configuration: the runtime
  // router 403s every write, including for the owner and a superuser
  // (core/runtime/router.py::_users_write_guard). Without this branch a
  // full_access user sees an enabled Save that cannot succeed.
  if (objectName === 'Roles') return false
  if (objectName === 'Users') return isUserAdmin(p)
  if (op === 'create') return canCreate(p, objectName)
  if (op === 'update') return canUpdate(p, objectName)
  return canDelete(p, objectName)
}

export function hasDesignerAccess(p: MyPermissions | undefined): boolean {
  return !!p?.designer_access
}
